import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Activity,
  Clock,
  Compass,
  Heart,
  TrendingUp,
  Zap,
  RefreshCw,
  Loader2,
  Download,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  mergeActivityWithDerived,
  samplesToChartData,
  isRunningSport,
  isCyclingSport,
  formatDuration,
  formatPace,
  formatSpeedKmh,
  formatSportType,
} from '@/lib/garminSamples';
import RunningDynamicsInsightCard from '@/components/garmin/RunningDynamicsInsightCard';
import RunningSignatureCard from '@/components/garmin/RunningSignatureCard';

const CHART_COLORS = {
  hr: 'hsl(340, 75%, 55%)',
  power: 'hsl(30, 80%, 55%)',
  speed: 'hsl(220, 70%, 50%)',
};

function MetricChart({ title, data, dataKey, color, unit, domain }) {
  const hasData = dataKey && data?.some((d) => d[dataKey] != null);
  if (!hasData) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={data} margin={{ top: 5, right: 12, left: -8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="timeMin"
              tickFormatter={(v) => `${v}m`}
              className="text-xs"
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              domain={domain}
              className="text-xs"
              tickLine={false}
              axisLine={false}
              stroke="hsl(var(--muted-foreground))"
              width={40}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.5rem',
              }}
              formatter={(value) => [`${Number(value).toFixed(1)} ${unit}`, title]}
              labelFormatter={(label) => `${label} min`}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              fill={color}
              fillOpacity={0.15}
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function getEnrichmentState(hasSamples, detailMetadata) {
  if (hasSamples) return 'ready';
  if (detailMetadata?.fit_storage_path) return 'fit_available';
  if (detailMetadata?.fit_download_error) return 'fit_failed';
  return 'no_data';
}

const ENRICH_LABELS = {
  fit_available: { text: 'FIT file stored. Click to parse timeline data.', badge: 'FIT available', variant: 'warning' },
  fit_failed: { text: 'FIT download failed during webhook. No file to parse.', badge: 'FIT failed', variant: 'destructive' },
  no_data: { text: 'No FIT file stored. Only activities recorded after the update can be parsed.', badge: 'No FIT', variant: 'destructive' },
};

const WearableActivityDetail = () => {
  const { userId, activityId } = useParams();
  const { can } = useAuth();
  const canEnrich = can('wearables.enrich');
  const [userName, setUserName] = useState('');
  const [activity, setActivity] = useState(null);
  const [samples, setSamples] = useState([]);
  const [detailsUnavailable, setDetailsUnavailable] = useState(false);
  const [detailMetadata, setDetailMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState(null);
  const [dynamicsRefreshKey, setDynamicsRefreshKey] = useState(0);

  const fetchActivityData = useCallback(async () => {
    if (!userId || !activityId) return;
    setLoading(true);

    const [profileRes, actRes] = await Promise.all([
      supabase.rpc('staff_get_user_profiles', { p_user_ids: [userId] }).single(),
      supabase
        .from('garmin_activities')
        .select(
          'id, garmin_activity_id, sport_type, started_at, duration_seconds, distance_meters, calories_kcal, avg_heart_rate_bpm, max_heart_rate_bpm, avg_speed_mps, avg_pace_sec_per_km, elevation_gain_meters, summary'
        )
        .eq('user_id', userId)
        .eq('id', activityId)
        .single(),
    ]);

    setUserName(profileRes.data?.full_name || 'Unknown');

    if (actRes.data) {
      const { data: detailRow, error: detailError } = await supabase
        .from('garmin_activity_details')
        .select('samples_5s, laps, zones, detail_metadata')
        .eq('user_id', userId)
        .eq('garmin_activity_id', actRes.data.garmin_activity_id)
        .maybeSingle();

      if (detailError) {
        console.error('Error fetching Garmin activity details:', detailError);
      }

      const sampleData = Array.isArray(detailRow?.samples_5s) ? detailRow.samples_5s : [];
      setDetailsUnavailable(!detailError && !sampleData.length);
      setSamples(sampleData);
      setDetailMetadata(detailRow?.detail_metadata || null);
      setActivity(mergeActivityWithDerived(actRes.data, sampleData));
    }

    setLoading(false);
  }, [userId, activityId]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  const handleEnrich = useCallback(async () => {
    if (!activityId || enriching) return;
    setEnriching(true);
    setEnrichError(null);

    try {
      const { data, error } = await supabase.functions.invoke('garmin-fit-enrich', {
        body: { activityId, mode: 'single' },
      });
      if (error) {
        const body = data ?? error?.context?.json ?? null;
        const reason = body?.error || body?.reason || error.message;
        throw new Error(reason);
      }
      if (data?.status === 'error' || data?.status === 'no_fit') throw new Error(data.reason || 'Parse failed');

      await fetchActivityData();
    } catch (err) {
      setEnrichError(err.message || 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  }, [activityId, enriching, fetchActivityData]);

  const chartData = useMemo(() => samplesToChartData(samples), [samples]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/user-activity/${userId}/wearable`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Wearable Data
          </Link>
        </Button>
        <Card className="mt-6">
          <CardContent className="p-8 text-center text-muted-foreground">
            Activity not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const activityName =
    activity.summary?.activityName || formatSportType(activity.sport_type);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
      <Helmet>
        <title>
          {activityName} · Wearable Data · {userName}
        </title>
      </Helmet>

      <Button variant="outline" size="sm" asChild>
        <Link to={`/user-activity/${userId}/wearable`}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Wearable Data
        </Link>
      </Button>

      <header>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
              <Activity className="h-6 w-6" />
              {activityName}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 capitalize">
              {formatSportType(activity.sport_type)} · {userName}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(activity.started_at).toLocaleString(undefined, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {activity.hasSamples && (
              <Badge variant="outline">{activity.sampleCount} samples</Badge>
            )}
            {canEnrich && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEnrich}
                disabled={enriching}
                title={enriching ? 'Parsing...' : 'Parse FIT file'}
              >
                {enriching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="ml-1.5 hidden sm:inline">
                  {enriching ? 'Parsing...' : 'Parse FIT'}
                </span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
        {activity.duration_seconds != null && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Duration
              </p>
              <p className="text-lg font-bold">{formatDuration(activity.duration_seconds)}</p>
            </CardContent>
          </Card>
        )}
        {activity.distance_meters != null && activity.distance_meters > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <Compass className="h-3 w-3" /> Distance
              </p>
              <p className="text-lg font-bold">
                {(activity.distance_meters / 1000).toFixed(2)} km
              </p>
            </CardContent>
          </Card>
        )}
        {activity.avg_heart_rate_bpm != null && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <Heart className="h-3 w-3" /> Avg HR
              </p>
              <p className="text-lg font-bold">
                {activity.avg_heart_rate_bpm}
                {activity.max_heart_rate_bpm ? (
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}
                    / {activity.max_heart_rate_bpm}
                  </span>
                ) : null}{' '}
                <span className="text-sm font-normal text-muted-foreground">bpm</span>
              </p>
            </CardContent>
          </Card>
        )}
        {isCyclingSport(activity.sport_type) && activity.avg_power_watts != null && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <Zap className="h-3 w-3" /> Avg Power
              </p>
              <p className="text-lg font-bold">
                {activity.avg_power_watts}
                {activity.max_power_watts ? (
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}
                    / {activity.max_power_watts}
                  </span>
                ) : null}{' '}
                <span className="text-sm font-normal text-muted-foreground">W</span>
              </p>
            </CardContent>
          </Card>
        )}
        {formatSpeedKmh(activity.avg_speed_mps) && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Avg Speed
              </p>
              <p className="text-lg font-bold">{formatSpeedKmh(activity.avg_speed_mps)}</p>
            </CardContent>
          </Card>
        )}
        {isRunningSport(activity.sport_type) && formatPace(activity.avg_pace_sec_per_km) && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Avg Pace
              </p>
              <p className="text-lg font-bold">{formatPace(activity.avg_pace_sec_per_km)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {isRunningSport(activity.sport_type) && (
        <>
          <Separator />
          <RunningSignatureCard
            userId={userId}
            garminActivityId={activity.garmin_activity_id}
            activityDbId={activityId}
            onAnalyzed={() => setDynamicsRefreshKey((key) => key + 1)}
          />
          <RunningDynamicsInsightCard
            userId={userId}
            garminActivityId={activity.garmin_activity_id}
            refreshKey={dynamicsRefreshKey}
          />
        </>
      )}

      {chartData.length > 0 ? (
        <>
          <Separator />
          <h2 className="text-sm font-medium text-muted-foreground">Activity timeline</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricChart
              title="Heart rate"
              data={chartData}
              dataKey="hr"
              color={CHART_COLORS.hr}
              unit="bpm"
              domain={['auto', 'auto']}
            />
            {isCyclingSport(activity.sport_type) && (
              <MetricChart
                title="Power"
                data={chartData}
                dataKey="power"
                color={CHART_COLORS.power}
                unit="W"
                domain={['auto', 'auto']}
              />
            )}
            <MetricChart
              title="Speed"
              data={chartData}
              dataKey="speedKmh"
              color={CHART_COLORS.speed}
              unit="km/h"
              domain={['auto', 'auto']}
            />
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground space-y-3">
            {(() => {
              const enrichState = getEnrichmentState(false, detailMetadata);
              const label = ENRICH_LABELS[enrichState];
              if (!label) return <p>No sample data available for this activity.</p>;
              return (
                <>
                  <Download className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p>{label.text}</p>
                  {enrichError && (
                    <p className="text-xs text-destructive">{enrichError}</p>
                  )}
                  {canEnrich && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleEnrich}
                      disabled={enriching}
                    >
                      {enriching ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Parsing FIT...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Parse FIT
                        </>
                      )}
                    </Button>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WearableActivityDetail;
